import React from "react";
import TopBar from "../TopBar";
import type { SegmentedControlOption } from "../SegmentedControl";
import type { FreshnessStampProps } from "../FreshnessStamp";
import "./ds.css";

export interface V2PageTabs<T extends string> {
  options: SegmentedControlOption<T>[];
  value: T;
  onChange: (v: T) => void;
  /** `topbar` (default): the segmented tabs in the top bar, as on Sales,
   *  Returns and Products. `page`: the underlined tab row under the
   *  masthead (mock finances `.ftabs`). */
  at?: "topbar" | "page";
}

export interface V2PageProps<T extends string = string> {
  /** The page's h1. No eyebrow above it: the brand is in the top-bar breadcrumb.
   *  Leave it out only when the content draws the page's one h1 itself (a
   *  document's cover, the brief): then there is no masthead. */
  title?: React.ReactNode;
  /** The line under the title (dates, what the page covers). */
  subtitle?: React.ReactNode;
  /** The page's one-sentence answer under the title, in large ink (mock
   *  home `.mast .ans`), written by rule from the numbers. */
  answer?: React.ReactNode;
  /** Right side of the masthead: a period control or other compact switch. */
  controls?: React.ReactNode;
  /** View tabs (Overview / Reports ...). */
  tabs?: V2PageTabs<T>;
  /** The freshness stamp in the top bar. */
  freshness?: FreshnessStampProps;
  /** The page has a tour (components/v2/tour): the top bar's "?" replays it. */
  onTour?: () => void;
  /** The to-do box. A row above the content below 1600px; the 380px sticky
   *  right rail from 1600px. */
  todos?: React.ReactNode;
  /** `after`: the to-dos sit under the KPI strip below 1600px (Products,
   *  Next shipment). Default `before`. */
  todosPlacement?: "before" | "after";
  /** The KPI strip (a `KpiStrip`). */
  kpis?: React.ReactNode;
  /** Everything else: cards, stacked 16px apart. */
  children?: React.ReactNode;
  /** The page landmark element. */
  as?: "div" | "main";
  /** Where tests and anchors find the page. */
  id?: string;
  /** The page is one full-height workspace edge to edge (onboarding's two
   *  panes, onboarding-vera-mock 2026-10-12): no column padding or width,
   *  the children fill the frame's main. No masthead: the content draws the h1. */
  fill?: boolean;
}

/**
 * V2Page: the one page shell for every v2 page (mock built-v12 `.page`,
 * `.mast`, `.layout`). It owns the column (max 2000px, padding 28/32/64,
 * phone 20/16/80), the masthead, the view tabs and the layout grid. A page
 * passes slots; it never sets its own padding, width or grid.
 *
 * Without `todos` and `kpis` the children follow the masthead directly
 * (reports views, settings-like pages), still inside the page column.
 */
export function V2Page<T extends string = string>({
  title,
  subtitle,
  answer,
  controls,
  tabs,
  freshness,
  onTour,
  todos,
  todosPlacement = "before",
  kpis,
  children,
  as: Tag = "div",
  id,
  fill,
}: V2PageProps<T>): React.ReactElement {
  const pageTabs = tabs && tabs.at === "page" ? tabs : null;
  const barTabs = tabs && tabs.at !== "page" ? tabs : undefined;
  const grid = todos != null || kpis != null;
  return (
    <Tag className="ds-page" data-v2-page="" data-fill={fill ? "" : undefined} id={id}>
      {(barTabs || freshness || onTour) && (
        <TopBar<T> tabs={barTabs} freshness={freshness} onTour={onTour} />
      )}
      {title != null && (
        <header className="ds-mast">
          <div className="ds-mast-row">
            <div>
              <h1>{title}</h1>
              {subtitle != null && <p className="ds-mast-meta">{subtitle}</p>}
              {answer != null && <p className="ds-mast-ans">{answer}</p>}
            </div>
            {controls != null && <div className="ds-mast-ctl">{controls}</div>}
          </div>
        </header>
      )}
      {pageTabs && (
        <nav className="ds-ptabs" role="tablist" aria-label="Page view" data-allow-scroll="tabs">
          {pageTabs.options.map((o) => (
            <button
              key={o.value}
              type="button"
              role="tab"
              aria-selected={o.value === pageTabs.value}
              onClick={() => pageTabs.onChange(o.value)}
            >
              {o.label}
            </button>
          ))}
        </nav>
      )}
      {grid ? (
        <div
          className="ds-layout"
          data-todos={todos == null ? "none" : todosPlacement}
        >
          {todos != null && (
            <aside className="ds-a-todo" aria-label="To do">
              {todos}
            </aside>
          )}
          {kpis != null && <div className="ds-a-nums">{kpis}</div>}
          <div className="ds-a-main">{children}</div>
        </div>
      ) : (
        children
      )}
    </Tag>
  );
}

/** Masthead controls stacked right-aligned (mock home `.mast-side`): a
 *  period control over a quiet link. Left-aligned on phones. */
export const MastSide: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => <div className="ds-mast-side">{children}</div>;

/** Cards stacked 16px apart, for a view body that is not the page grid. */
export const Stack: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => <div className="ds-a-main">{children}</div>;

/** Two columns of cards (mock `.more`); one column under 960px. With
 *  `phoneOrder`, the one phone column follows each card's `phoneOrder`
 *  (the mock's page order) instead of column 1 then column 2. */
export const Columns: React.FC<{
  children: React.ReactNode;
  id?: string;
  phoneOrder?: boolean;
}> = ({ children, id, phoneOrder }) => (
  <section
    className="ds-cols"
    id={id}
    data-phone-order={phoneOrder ? "" : undefined}
  >
    {children}
  </section>
);
export const Column: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => <div className="ds-col">{children}</div>;

/** A loading, empty or error message in place of the page body. */
export function PageState<T extends string = string>({
  title,
  children,
  actions,
  tabs,
  freshness,
}: {
  title?: string;
  children?: React.ReactNode;
  actions?: React.ReactNode;
  /** Keep the page's top-bar tabs and stamp while it loads. */
  tabs?: V2PageTabs<T>;
  freshness?: FreshnessStampProps;
}): React.ReactElement {
  return (
    <div className="ds-page" data-v2-page="" data-state="">
      {(tabs || freshness) && <TopBar<T> tabs={tabs} freshness={freshness} />}
      <div className="ds-empty" role="status">
        {title && <h1>{title}</h1>}
        {children != null && <p>{children}</p>}
        {actions != null && <div className="ds-empty-go">{actions}</div>}
      </div>
    </div>
  );
}

export default V2Page;
